import { Box, IconButton, Typography } from "@mui/material";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import { MouseEvent } from "react";
import { Task } from "../../api/Tasks";
import { ProjectCode } from "../project/ProjectCode";
import { CalendarTask } from "./calendar/CalendarTask";

// UnscheduledGroup is the tasks of one project with no due date, split by goal. A null
// project holds the tasks filed under nothing.
export type UnscheduledGroup = {
    project: number | null;
    name: string;
    code: string;
    color: string;
    count: number;
    goals: { goal: number | null; name: string; tasks: Task[] }[];
}

// groupUnscheduled lays the tasks out project by project, then goal by goal, each by
// name. "No goal" closes each project, and "No project" comes last.
export function groupUnscheduled(tasks: Task[]): UnscheduledGroup[] {
    const byProject = new Map<number, Task[]>();
    const loose: Task[] = [];

    for (const task of tasks) {
        if (!task.project) {
            loose.push(task);
            continue;
        }

        byProject.set(task.project, (byProject.get(task.project) || []).concat(task));
    }

    const groups: UnscheduledGroup[] = Array.from(byProject.entries()).map(([project, rows]) => {
        const byGoal = new Map<number, Task[]>();
        const noGoal: Task[] = [];

        for (const task of rows) {
            if (task.goal) byGoal.set(task.goal, (byGoal.get(task.goal) || []).concat(task));
            else noGoal.push(task);
        }

        const goals = Array.from(byGoal.entries())
            .map(([goal, items]) => ({ goal: goal as number | null, name: items[0].goalName, tasks: items }))
            .sort((a, b) => a.name.localeCompare(b.name));

        if (noGoal.length > 0) goals.push({ goal: null, name: "No goal", tasks: noGoal });

        return {
            project: project,
            name: rows[0].projectName,
            code: rows[0].projectCode,
            color: rows[0].projectColor,
            count: rows.length,
            goals: goals,
        };
    }).sort((a, b) => a.name.localeCompare(b.name));

    if (loose.length > 0) {
        groups.push({
            project: null,
            name: "No project",
            code: "",
            color: "",
            count: loose.length,
            goals: [{ goal: null, name: "", tasks: loose }],
        });
    }

    return groups;
}

// UnscheduledPanel lists the open tasks with no due date in the side panel. A task is
// dragged from here onto a calendar day to schedule it.
export function UnscheduledPanel(props: {
    tasks: Task[];
    selectedId?: number;
    saving?: boolean;
    onClose: () => void;
    onOpen: (task: Task) => void;
    onContextMenu: (task: Task, e: MouseEvent) => void;
    onToggle: (task: Task, checked: boolean) => void;
    onDragStart: (task: Task) => void;
    onDragEnd: () => void;
}) {
    const groups = groupUnscheduled(props.tasks);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
            <Box sx={{
                display: "flex", alignItems: "center", gap: 1, flexShrink: 0,
                pl: 3, pr: 1, py: 1,
                bgcolor: "grey.50",
                borderBottom: 1, borderColor: "divider",
            }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>Unscheduled</Typography>
                <IconButton size="small" onClick={props.onClose}>
                    <KeyboardDoubleArrowRight fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 3, py: 2 }}>
                {groups.length === 0 && (
                    <>
                        <Typography variant="body2" color="text.secondary">No unscheduled tasks</Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                            A task with no due date shows here. Drag it onto a day to schedule it.
                        </Typography>
                    </>
                )}

                {groups.map(group => (
                    <Box key={group.project || "none"} sx={{ pb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, pb: 0.5 }}>
                            <ProjectCode code={group.code} color={group.color} />
                            <Typography variant="subtitle2" noWrap sx={{ flex: 1, minWidth: 0 }}>{group.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{group.count}</Typography>
                        </Box>

                        {group.goals.map(goal => (
                            <Box key={goal.goal || "none"} sx={{ pl: goal.name ? 1 : 0, pb: 1 }}>
                                {goal.name && (
                                    <Typography variant="caption" color="text.secondary" component="div" sx={{ pb: 0.5 }}>
                                        {goal.name}
                                    </Typography>
                                )}

                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                    {goal.tasks.map(task => (
                                        <CalendarTask
                                            key={task.id}
                                            task={task}
                                            showCopy
                                            selected={task.id === props.selectedId}
                                            disabled={props.saving}
                                            onClick={() => props.onOpen(task)}
                                            onContextMenu={e => props.onContextMenu(task, e)}
                                            onToggle={checked => props.onToggle(task, checked)}
                                            draggable
                                            onDragStart={e => {
                                                e.dataTransfer.effectAllowed = "move";
                                                e.dataTransfer.setData("text/plain", String(task.id));
                                                props.onDragStart(task);
                                            }}
                                            onDragEnd={props.onDragEnd}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
